import { MaterialTopTabBarProps } from '@react-navigation/material-top-tabs';
import React, { useEffect, useRef, useState } from 'react';
import {
  Animated,
  LayoutChangeEvent,
  Platform,
  StyleProp,
  StyleSheet,
  TouchableOpacity,
  ViewStyle,
} from 'react-native';

import Icon from '@components/shared/Icon/Icon';
import { useFontScale } from '@hooks/useFontScale';

import theme, { getScaledSize } from '../../../themes/theme';
import { Box, Text } from '../Restyle';

export type CustomerTabbarProps = {
  spacing?: number;
  initialRouteName?: string;
  contentContainerStyle?: StyleProp<ViewStyle>;
  memberCount?: number;
  disabled?: boolean;
  checklistTabRef?: React.RefObject<any>;
  // TA-10818: web has no equivalent of native ProjectDetail's unread-activity
  // dot -- the Activity Log tab here never surfaced unreadAuditsCount at all.
  hasUnreadActivity?: boolean;
};

type IProps = MaterialTopTabBarProps & CustomerTabbarProps;

const CustomTabBarProject = ({
  state,
  descriptors,
  navigation,
  spacing = Platform.OS === 'web' ? theme.spacing.m : 10,
  contentContainerStyle,
  memberCount = 1,
  disabled = false,
  checklistTabRef,
  hasUnreadActivity = false,
}: IProps): JSX.Element => {
  const scaleX_value = useRef(new Animated.Value(0)).current;
  const translateX_value = useRef(new Animated.Value(0)).current;
  const hoverScaleX_value = useRef(new Animated.Value(0)).current;
  const hoverTranslateX_value = useRef(new Animated.Value(0)).current;
  let isAnimating = false;

  type Position = {
    index: number;
    width: number;
    x: number;
  };

  const [positions, setPositions] = useState<Position[]>([]);
  const [hoveredTabIndex, setHoveredTabIndex] = useState<number | null>(null);
  const { fontScale } = useFontScale();

  // get the label widths on mount
  const onLayout = (event: LayoutChangeEvent, index: number) => {
    const { width, x } = event.nativeEvent.layout;

    const calcX = x + width / 2;

    setPositions((pos) => {
      const filtered = pos.filter((p) => p.index !== index);
      return [...filtered, { index: index, width: width, x: calcX }];
    });

    if (state.index === index) {
      scaleX_value.setValue(width);
      translateX_value.setValue(calcX);
    }
  };

  useEffect(() => {
    const newPos = positions.findIndex((i) => i.index === state.index);
    if (newPos !== -1 && positions.length > 0) {
      animate(newPos);
    }
  }, [state, positions]);

  // Handle hover indicator position (web only, no animation)
  useEffect(() => {
    if (Platform.OS !== 'web') return;

    if (hoveredTabIndex !== null && hoveredTabIndex !== state.index) {
      const hoveredPos = positions.findIndex(
        (i) => i.index === hoveredTabIndex
      );
      if (hoveredPos !== -1 && positions.length > 0) {
        const position = positions[hoveredPos];
        hoverTranslateX_value.setValue(getNewPosX(position));
        hoverScaleX_value.setValue(position.width);
      }
    } else {
      // Hide hover indicator when not hovering or hovering over active tab
      hoverTranslateX_value.setValue(0);
      hoverScaleX_value.setValue(0);
    }
  }, [hoveredTabIndex, state.index, positions]);

  const getNewPosX = (position: Position) => {
    if (
      Platform.OS !== 'web' &&
      spacing !== theme.spacing.m &&
      spacing !== theme.spacing.l &&
      position.index !== 0
    ) {
      return position.x - theme.spacing.s * position.index;
    } else {
      // https://tasktag.atlassian.net/browse/TA-6136
      // this hard code 47 is from Android phone when the app works. because this bug only happened on Android device.
      return position?.x ?? 47;
    }
  };

  const animate = (newPos: number) => {
    isAnimating = true;
    Animated.parallel([
      Animated.timing(translateX_value, {
        toValue: getNewPosX(positions[newPos]),
        duration: 100,
        useNativeDriver: true,
      }),
      Animated.timing(scaleX_value, {
        toValue: positions[newPos].width,
        duration: 100,
        useNativeDriver: true,
      }),
    ]).start(() => {
      isAnimating = false;
    });
  };

  const getTabColor = (isFocused: boolean, isHovered: boolean) =>
    isFocused || isHovered ? 'greenSecondary' : 'textSecondary';

  const getIcon = (label) => {
    switch (label) {
      case 'Tasks':
        return 'TTHashtag';
      case 'Checklist':
        return 'FileText';
      case 'Files & Media':
        return 'FilesImages';
      case 'Activity Log':
        return 'ActivityDot';
      case 'Members':
        return 'Users';
    }
  };

  // basic labels as suggested by react navigation
  const labels = state.routes.map((route, index) => {
    const { options } = descriptors[route.key];
    const label =
      (route.params as any)?.tabBarLabel || (options as any).tabBarLabel;
    const isFocused = state.index === index;
    const isHovered = hoveredTabIndex === index;

    const onPress = () => {
      if (disabled || isAnimating) {
        return;
      }
      const event = navigation.emit({
        type: 'tabPress',
        target: route.key,
        canPreventDefault: true,
      });

      if (!isFocused && !event.defaultPrevented) {
        // @ts-ignore
        navigation.navigate({ name: route.name, merge: true });
      }
    };

    return (
      <Box
        key={route.key}
        ref={route.name === 'checklist' ? checklistTabRef : undefined}
        alignItems='center'
        justifyContent='center'
        onLayout={(event) => onLayout(event, index)}>
        <TouchableOpacity
          accessibilityRole='button'
          accessibilityState={isFocused ? { selected: true } : {}}
          onPress={onPress}
          disabled={disabled}
          style={[
            { paddingHorizontal: spacing },
            disabled ? { opacity: 0.5, cursor: 'default' } : undefined,
          ]}
          {...(Platform.OS === 'web' && {
            onMouseEnter: () => !disabled && setHoveredTabIndex(index),
            onMouseLeave: () => setHoveredTabIndex(null),
          })}>
          <Box
            accessibilityLabel={
              // Copilot review, PR #5905, round 7: the dot below is visual
              // only -- a screen reader has no way to know Activity Log has
              // unread activity unless the tab's own accessible label says
              // so.
              label === 'Activity Log' && !disabled && hasUnreadActivity
                ? `${label}, unread`
                : label
            }
            paddingVertical='xs'
            flexDirection='row'
            alignItems='center'>
            <Icon
              name={getIcon(label)}
              color={disabled ? 'grey03' : getTabColor(isFocused, isHovered)}
              height={16}
              width={16}
              marginEnd='xs'
            />
            <Text
              marginEnd='xs'
              variant='labelSmall'
              color={disabled ? 'grey03' : getTabColor(isFocused, isHovered)}>
              {label}
            </Text>
            {label === 'Activity Log' && !disabled && hasUnreadActivity && (
              <Box
                testID='project-activity-unread-dot'
                marginStart='xxs'
                width={theme.spacing.xs}
                height={theme.spacing.xs}
                backgroundColor='greenSecondary'
                borderRadius='xs'
              />
            )}
            {label === 'Members' && !disabled && memberCount > 0 && (
              <Box
                width={(memberCount > 9 ? 21 : 18) * fontScale}
                height={(memberCount > 9 ? 21 : 18) * fontScale}
                backgroundColor={
                  isFocused || isHovered ? 'greenSecondary' : 'black'
                }
                justifyContent='center'
                alignItems='center'
                style={{ borderRadius: 18 * fontScale }}>
                <Text
                  variant='metadataSecondary'
                  fontWeight='500'
                  color='white'
                  textAlign='center'>
                  {memberCount > 99 ? 99 : memberCount}
                </Text>
              </Box>
            )}
          </Box>
        </TouchableOpacity>
      </Box>
    );
  });

  return (
    <Box height={getScaledSize(56)} flex={1}>
      <Animated.View style={[styles.container, contentContainerStyle]}>
        {labels}
        <Animated.View
          testID='project-tab-bar-indicator'
          style={[
            disabled ? styles.disabledIndicator : styles.indicator,
            {
              transform: [
                { translateX: translateX_value },
                { scaleX: scaleX_value },
              ],
            },
          ]}
        />
        {/* Hover indicator (web only, only when hovering over non-active tab) */}
        {Platform.OS === 'web' &&
          hoveredTabIndex !== null &&
          hoveredTabIndex !== state.index && (
            <Animated.View
              style={[
                disabled ? styles.disabledIndicator : styles.indicator,
                {
                  transform: [
                    { translateX: hoverTranslateX_value },
                    { scaleX: hoverScaleX_value },
                  ],
                },
              ]}
            />
          )}
      </Animated.View>
    </Box>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    height: getScaledSize(56),
  },
  indicator: {
    backgroundColor: theme.colors.greenSecondary,
    height: 1,
    position: 'absolute',
    bottom: 0,
    right: 0,
    left: 0,
    width: 1,
  },
  disabledIndicator: {
    backgroundColor: theme.colors.grey02,
    height: 1,
    position: 'absolute',
    bottom: 0,
    right: 0,
    left: 0,
    width: 1,
  },
});

export default CustomTabBarProject;
